/**
 * AuthContext + useAuth — CF Access redirect-based auth flow.
 *
 * Implementa decisiones G0 FWA-PWA-022:
 * - D1: Fetch /v1/auth/verify para user claims (cookie HttpOnly)
 * - D2: In-memory state (no sessionStorage V1; reduce XSS surface)
 * - D5: Logout via /cdn-cgi/access/logout
 * - D6: 401 handler trigger logout (via window.__sisrep_auth global)
 */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { env } from '../env.js';
import { apiPost } from '../lib/api.js';

// ─── Tipos ────────────────────────────────────────────────────────────────

export interface User {
  email: string;
  rol: string;
  rama: string | null;
  distrito_id: string;
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; user: User }
  | { status: 'error'; error: string };

interface AuthActions {
  login: (returnTo?: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

interface AuthContextValue extends AuthActions {
  state: AuthState;
}

// ─── Context ──────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

// ─── Global escape hatch para api.ts 401 handler (D6) ─────────────────────

declare global {
  interface Window {
    __sisrep_auth?:
      | {
          logout: () => void;
        }
      | undefined;
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refreshUser = useCallback(async () => {
    try {
      const res = await apiPost<User, Record<string, never>>('/v1/auth/verify', {});
      setState({ status: 'authenticated', user: res.data });
    } catch (err) {
      const status = (err as { status?: number }).status;
      // V1.0.1 patch: tratar network/CORS errors como unauthenticated.
      // Cuando el browser NO tiene cookie del Worker domain (cross-app CF Access),
      // el fetch falla con TypeError "Failed to fetch" (status undefined) por CORS
      // del 302 redirect a cloudflareaccess.com. Eso es estado normal pre-login.
      if (status === 401 || status === undefined) {
        setState({ status: 'unauthenticated' });
      } else {
        setState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown auth error',
        });
      }
    }
  }, []);

  const login = useCallback((returnTo?: string) => {
    // CF Access intercepts unauthenticated request → IdP redirect → callback
    const verifyUrl = new URL('/v1/auth/verify', env.WORKER_URL);
    if (returnTo) verifyUrl.searchParams.set('return_to', returnTo);
    window.location.href = verifyUrl.toString();
  }, []);

  const logout = useCallback(() => {
    // PII tier Crítico (FWA-PWA-029): clear outbox IndexedDB antes de redirect.
    // Fire-and-forget — no bloquea logout si IDB falla. Dynamic import evita
    // top-level coupling con auth context.
    void import('../lib/outbox/db.js')
      .then((m) => m.clearAll())
      .catch(() => {
        // IDB no disponible o ya cleared — logout debe proceder igual
      });
    // CF Access logout endpoint clears the CF_Authorization cookie
    const logoutUrl = new URL('/cdn-cgi/access/logout', env.WORKER_URL);
    logoutUrl.searchParams.set('returnTo', '/login');
    setState({ status: 'unauthenticated' });
    window.location.href = logoutUrl.toString();
  }, []);

  // Expose logout globally para api.ts 401 handler (D6)
  useEffect(() => {
    window.__sisrep_auth = { logout };
    return () => {
      window.__sisrep_auth = undefined;
    };
  }, [logout]);

  // Initial verify on mount
  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout, refreshUser }),
    [state, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
